import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  ExpandMore,
  ExpandLess,
  FilterList,
  Download,
  Print,
  TableChart,
  Timeline,
  Assessment,
  TrendingUp,
  Warning,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { getLabReports, getLabResults, getTestDefinitions } from '../../../services/labReportsService';

// Mock data for demo purposes
const generateMockLabData = (patientId) => {
  const mockReports = [
    {
      id: 'report-1',
      procedure_report_id: 'RPT-001',
      procedure_order_id: 'ORD-001',
      patientId: patientId,
      provider_id: 'Dr. Smith',
      date_collected: subDays(new Date(), 5),
      date_report: subDays(new Date(), 4),
      review_status: 'final',
      comments: 'Complete Blood Count Panel'
    },
    {
      id: 'report-2', 
      procedure_report_id: 'RPT-002',
      procedure_order_id: 'ORD-002',
      patientId: patientId,
      provider_id: 'Dr. Johnson',
      date_collected: subDays(new Date(), 15),
      date_report: subDays(new Date(), 14),
      review_status: 'final',
      comments: 'Basic Metabolic Panel'
    },
    {
      id: 'report-3',
      procedure_report_id: 'RPT-003', 
      procedure_order_id: 'ORD-003',
      patientId: patientId,
      provider_id: 'Dr. Wilson',
      date_collected: subDays(new Date(), 30),
      date_report: subDays(new Date(), 29),
      review_status: 'final',
      comments: 'Lipid Panel'
    },
    {
      id: 'report-4',
      procedure_report_id: 'RPT-004', 
      procedure_order_id: 'ORD-004',
      patientId: patientId,
      provider_id: 'Dr. Brown',
      date_collected: subDays(new Date(), 45),
      date_report: subDays(new Date(), 44),
      review_status: 'final',
      comments: 'Follow-up Labs'
    }
  ];

  const mockResults = [
    // Report 1 Results (CBC)
    { id: 'res-1', procedure_result_id: 'RES-001', procedure_report_id: 'RPT-001', test_code: 'WBC', test_name: 'White Blood Cells', result: '7.2', units: '10^3/μL', ref_range: '4.0-11.0', abnormal: 'N', result_status: 'final' },
    { id: 'res-2', procedure_result_id: 'RES-002', procedure_report_id: 'RPT-001', test_code: 'RBC', test_name: 'Red Blood Cells', result: '4.8', units: '10^6/μL', ref_range: '4.2-5.4', abnormal: 'N', result_status: 'final' },
    { id: 'res-3', procedure_result_id: 'RES-003', procedure_report_id: 'RPT-001', test_code: 'HGB', test_name: 'Hemoglobin', result: '14.2', units: 'g/dL', ref_range: '12.0-15.5', abnormal: 'N', result_status: 'final' },
    { id: 'res-4', procedure_result_id: 'RES-004', procedure_report_id: 'RPT-001', test_code: 'HCT', test_name: 'Hematocrit', result: '42.1', units: '%', ref_range: '36.0-46.0', abnormal: 'N', result_status: 'final' },

    // Report 2 Results (BMP)
    { id: 'res-5', procedure_result_id: 'RES-005', procedure_report_id: 'RPT-002', test_code: 'GLU', test_name: 'Glucose', result: '105', units: 'mg/dL', ref_range: '70-100', abnormal: 'H', result_status: 'final' },
    { id: 'res-6', procedure_result_id: 'RES-006', procedure_report_id: 'RPT-002', test_code: 'BUN', test_name: 'Blood Urea Nitrogen', result: '18', units: 'mg/dL', ref_range: '7-20', abnormal: 'N', result_status: 'final' },
    { id: 'res-7', procedure_result_id: 'RES-007', procedure_report_id: 'RPT-002', test_code: 'CRE', test_name: 'Creatinine', result: '1.1', units: 'mg/dL', ref_range: '0.7-1.3', abnormal: 'N', result_status: 'final' },
    { id: 'res-8', procedure_result_id: 'RES-008', procedure_report_id: 'RPT-002', test_code: 'NA', test_name: 'Sodium', result: '142', units: 'mmol/L', ref_range: '136-145', abnormal: 'N', result_status: 'final' },

    // Report 3 Results (Lipids)
    { id: 'res-9', procedure_result_id: 'RES-009', procedure_report_id: 'RPT-003', test_code: 'CHOL', test_name: 'Total Cholesterol', result: '220', units: 'mg/dL', ref_range: '<200', abnormal: 'H', result_status: 'final' },
    { id: 'res-10', procedure_result_id: 'RES-010', procedure_report_id: 'RPT-003', test_code: 'HDL', test_name: 'HDL Cholesterol', result: '45', units: 'mg/dL', ref_range: '>40', abnormal: 'N', result_status: 'final' },
    { id: 'res-11', procedure_result_id: 'RES-011', procedure_report_id: 'RPT-003', test_code: 'LDL', test_name: 'LDL Cholesterol', result: '150', units: 'mg/dL', ref_range: '<100', abnormal: 'H', result_status: 'final' },
    { id: 'res-12', procedure_result_id: 'RES-012', procedure_report_id: 'RPT-003', test_code: 'TRIG', test_name: 'Triglycerides', result: '125', units: 'mg/dL', ref_range: '<150', abnormal: 'N', result_status: 'final' },

    // Additional historical data for trends
    { id: 'res-13', procedure_result_id: 'RES-013', procedure_report_id: 'RPT-004', test_code: 'GLU', test_name: 'Glucose', result: '95', units: 'mg/dL', ref_range: '70-100', abnormal: 'N', result_status: 'final' },
    { id: 'res-14', procedure_result_id: 'RES-014', procedure_report_id: 'RPT-004', test_code: 'HGB', test_name: 'Hemoglobin', result: '13.8', units: 'g/dL', ref_range: '12.0-15.5', abnormal: 'N', result_status: 'final' },
  ];

  const mockTestDefinitions = [
    { id: 'def-1', name: 'Complete Blood Count', testType: 'Lab', active: true, analytes: ['WBC', 'RBC', 'HGB', 'HCT'], units: 'Various', referenceRangeLow: 'Various', referenceRangeHigh: 'Various' },
    { id: 'def-2', name: 'Basic Metabolic Panel', testType: 'Lab', active: true, analytes: ['GLU', 'BUN', 'CRE', 'NA'], units: 'Various', referenceRangeLow: 'Various', referenceRangeHigh: 'Various' },
    { id: 'def-3', name: 'Lipid Panel', testType: 'Lab', active: true, analytes: ['CHOL', 'HDL', 'LDL', 'TRIG'], units: 'mg/dL', referenceRangeLow: 'Various', referenceRangeHigh: 'Various' }
  ];

  return { mockReports, mockResults, mockTestDefinitions };
};

// Custom hook for lab data
const useLabData = (patientId, dateRange) => {
  const [reports, setReports] = useState([]);
  const [results, setResults] = useState([]);
  const [testDefinitions, setTestDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLabData = async () => {
      console.log('fetchLabData called with patientId:', patientId);
      if (!patientId) {
        console.log('No patientId provided, exiting fetchLabData');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        console.log('Starting to fetch lab data for patient:', patientId);

        // Try to fetch real data first
        try {
          // Use service layer to fetch lab data
          const reportsData = await getLabReports(patientId, {
            startDate: dateRange.start,
            endDate: dateRange.end
          });

          // Fetch all results for these reports
          let allResults = [];
          if (reportsData.length > 0) {
            const reportIds = reportsData.map(r => r.procedure_report_id);
            allResults = await getLabResults(reportIds);
          }

          // Fetch test definitions for lab tests
          const testDefsData = await getTestDefinitions({
            testType: 'Lab',
            active: true
          });

          // If we have real data, use it
          if (reportsData.length > 0 || allResults.length > 0) {
            setReports(reportsData);
            setResults(allResults);
            setTestDefinitions(testDefsData);
          } else {
            throw new Error('No data found');
          }
        } catch (firebaseError) {
          // If Firebase fails (permissions, no data, etc.), use mock data
          console.warn('Firebase data unavailable, using mock data:', firebaseError.message);
          const { mockReports, mockResults, mockTestDefinitions } = generateMockLabData(patientId);
          
          // Filter by date range if specified
          let filteredReports = mockReports;
          if (dateRange.start && dateRange.end) {
            filteredReports = mockReports.filter(report => {
              const reportDate = new Date(report.date_report);
              return reportDate >= startOfDay(dateRange.start) && reportDate <= endOfDay(dateRange.end);
            });
          }

          setReports(filteredReports);
          setResults(mockResults.filter(result => 
            filteredReports.some(report => report.procedure_report_id === result.procedure_report_id)
          ));
          setTestDefinitions(mockTestDefinitions);
          setError('Using demo data - Firebase permissions may be required for real data');
        }
      } catch (err) {
        console.error('Error in fetchLabData:', err);
        setError('Failed to load lab data');
      } finally {
        setLoading(false);
      }
    };

    fetchLabData();
  }, [patientId, dateRange]);

  return { reports, results, testDefinitions, loading, error };
};

// Individual tab components
const TrendsTab = ({ results, reports, testDefinitions, dateRange }) => {
  const [selectedAnalytes, setSelectedAnalytes] = useState([]);
  const [expandedTests, setExpandedTests] = useState(new Set());

  // Organize data by analyte/test code
  const analytesData = useMemo(() => {
    const dataMap = new Map();
    
    results.forEach(result => {
      const report = reports.find(r => r.procedure_report_id === result.procedure_report_id);
      if (!report) return;

      const key = result.test_code;
      if (!dataMap.has(key)) {
        dataMap.set(key, {
          testCode: result.test_code,
          testName: result.test_name || result.test_code,
          units: result.units,
          referenceRange: result.ref_range,
          values: []
        });
      }

      dataMap.get(key).values.push({
        value: result.result,
        date: report.date_report,
        abnormal: result.abnormal,
        status: result.result_status,
        reportId: report.procedure_report_id
      });
    });

    // Sort values by date for each analyte
    dataMap.forEach(analyte => {
      analyte.values.sort((a, b) => new Date(a.date) - new Date(b.date));
    });

    return Array.from(dataMap.values());
  }, [results, reports]);

  // Create 2D table data: rows = test codes, columns = dates
  const tableData = useMemo(() => {
    if (analytesData.length === 0) return { dates: [], rows: [] };

    // Get all unique dates
    const allDates = new Set();
    analytesData.forEach(analyte => {
      analyte.values.forEach(value => {
        allDates.add(format(new Date(value.date), 'yyyy-MM-dd'));
      });
    });

    const sortedDates = Array.from(allDates).sort();

    // Create table rows
    const rows = analytesData.map(analyte => {
      const row = {
        testCode: analyte.testCode,
        testName: analyte.testName,
        units: analyte.units,
        referenceRange: analyte.referenceRange,
        values: {}
      };

      analyte.values.forEach(value => {
        const dateKey = format(new Date(value.date), 'yyyy-MM-dd');
        row.values[dateKey] = value;
      });

      return row;
    });

    return { dates: sortedDates, rows };
  }, [analytesData]);

  const getValueColor = (value) => {
    if (!value?.abnormal) return 'inherit';
    switch (value.abnormal.toUpperCase()) {
      case 'H':
      case 'HH':
        return '#d32f2f'; // High - red
      case 'L':
      case 'LL':
        return '#1976d2'; // Low - blue  
      case 'C':
        return '#f57c00'; // Critical - orange
      default:
        return 'inherit';
    }
  };

  const getValueIcon = (value) => {
    if (!value?.abnormal) return <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />;
    switch (value.abnormal.toUpperCase()) {
      case 'H':
      case 'HH':
      case 'L':
      case 'LL':
        return <Warning sx={{ fontSize: 16, color: 'warning.main' }} />;
      case 'C':
        return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Filter Controls */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Lab Values Trends
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Rows represent test analytes, columns represent collection dates. Click on values to see details.
        </Typography>
        
        {analytesData.length > 5 && (
          <FormControl sx={{ minWidth: 200, mt: 2 }}>
            <InputLabel>Filter Tests</InputLabel>
            <Select
              multiple
              value={selectedAnalytes}
              onChange={(e) => setSelectedAnalytes(e.target.value)}
              label="Filter Tests"
            >
              {analytesData.map(analyte => (
                <MenuItem key={analyte.testCode} value={analyte.testCode}>
                  {analyte.testName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Card>

      {/* 2D Results Table */}
      {tableData.dates.length > 0 ? (
        <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 150, fontWeight: 'bold', bgcolor: 'grey.100' }}>
                  Test Name
                </TableCell>
                <TableCell sx={{ minWidth: 100, fontWeight: 'bold', bgcolor: 'grey.100' }}>
                  Units
                </TableCell>
                <TableCell sx={{ minWidth: 120, fontWeight: 'bold', bgcolor: 'grey.100' }}>
                  Reference Range
                </TableCell>
                {tableData.dates.map(date => (
                  <TableCell 
                    key={date} 
                    sx={{ 
                      minWidth: 120, 
                      fontWeight: 'bold', 
                      bgcolor: 'grey.100',
                      writingMode: 'vertical-lr',
                      textOrientation: 'mixed'
                    }}
                  >
                    {format(new Date(date), 'MM/dd/yy')}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.rows
                .filter(row => selectedAnalytes.length === 0 || selectedAnalytes.includes(row.testCode))
                .map((row, index) => (
                <TableRow key={row.testCode} sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                  <TableCell sx={{ fontWeight: 'medium' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.testName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {row.testCode}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.units || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {row.referenceRange || '-'}
                    </Typography>
                  </TableCell>
                  {tableData.dates.map(date => {
                    const value = row.values[date];
                    return (
                      <TableCell key={`${row.testCode}-${date}`}>
                        {value ? (
                          <Tooltip 
                            title={
                              <Box>
                                <Typography variant="caption">
                                  {value.value} {row.units}
                                </Typography>
                                <br />
                                <Typography variant="caption">
                                  Status: {value.status}
                                </Typography>
                                {value.abnormal && (
                                  <>
                                    <br />
                                    <Typography variant="caption">
                                      Flag: {value.abnormal}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            }
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 0.5,
                              color: getValueColor(value),
                              cursor: 'pointer'
                            }}>
                              {getValueIcon(value)}
                              <Typography variant="body2" sx={{ fontWeight: value.abnormal ? 'bold' : 'normal' }}>
                                {value.value}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">No lab results found for the selected date range.</Alert>
      )}
    </Box>
  );
};

const ReportsTab = ({ reports, results }) => {
  const [expandedReport, setExpandedReport] = useState(null);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'final': return 'success';
      case 'preliminary': return 'warning';
      case 'corrected': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getAbnormalColor = (flag) => {
    if (!flag) return 'default';
    switch (flag.toUpperCase()) {
      case 'H':
      case 'HH':
      case 'L':
      case 'LL':
        return 'warning';
      case 'C':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Lab Reports by Collection Date
      </Typography>
      
      {reports.map(report => {
        const reportResults = results.filter(r => r.procedure_report_id === report.procedure_report_id);
        const isExpanded = expandedReport === report.id;
        
        return (
          <Card key={report.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  Order ID: {report.procedure_order_id}
                </Typography>
                <IconButton 
                  onClick={() => setExpandedReport(isExpanded ? null : report.id)}
                  size="small"
                >
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Collection Date</Typography>
                  <Typography variant="body2">
                    {format(new Date(report.date_collected), 'MM/dd/yyyy HH:mm')}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Report Date</Typography>
                  <Typography variant="body2">
                    {format(new Date(report.date_report), 'MM/dd/yyyy HH:mm')}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Provider</Typography>
                  <Typography variant="body2">{report.provider_id || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Status</Typography>
                  <Chip 
                    label={report.review_status || 'Unknown'} 
                    size="small"
                    color={getStatusColor(report.review_status)}
                  />
                </Grid>
              </Grid>

              <Collapse in={isExpanded}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Test Results ({reportResults.length})
                </Typography>
                
                {reportResults.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Test</TableCell>
                          <TableCell>Result</TableCell>
                          <TableCell>Units</TableCell>
                          <TableCell>Reference Range</TableCell>
                          <TableCell>Flag</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportResults.map(result => (
                          <TableRow key={result.id}>
                            <TableCell>{result.test_code}</TableCell>
                            <TableCell sx={{ fontWeight: result.abnormal ? 'bold' : 'normal' }}>
                              {result.result}
                            </TableCell>
                            <TableCell>{result.units}</TableCell>
                            <TableCell>{result.ref_range}</TableCell>
                            <TableCell>
                              <Chip 
                                label={result.abnormal || 'N'} 
                                size="small"
                                color={getAbnormalColor(result.abnormal)}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={result.result_status} 
                                size="small"
                                color={getStatusColor(result.result_status)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No results available for this report
                  </Typography>
                )}
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
      
      {reports.length === 0 && (
        <Alert severity="info">No lab reports found for the selected date range.</Alert>
      )}
    </Box>
  );
};

const SummaryTab = ({ reports, results, testDefinitions }) => {
  const summaryData = useMemo(() => {
    const summary = {
      totalReports: reports.length,
      totalResults: results.length,
      abnormalResults: results.filter(r => r.abnormal && r.abnormal !== 'N').length,
      criticalResults: results.filter(r => r.abnormal === 'C').length,
      recentResults: results.filter(r => {
        const resultDate = reports.find(rep => rep.procedure_report_id === r.procedure_report_id)?.date_report;
        return resultDate && new Date(resultDate) > subDays(new Date(), 30);
      }).length,
      testTypes: new Set(results.map(r => r.test_code)).size
    };
    return summary;
  }, [reports, results]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Lab Results Summary
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary">
                {summaryData.totalReports}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Reports
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TableChart sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {summaryData.totalResults}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Test Results
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Warning sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {summaryData.abnormalResults}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Abnormal Results
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" color="error.main">
                {summaryData.criticalResults}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Critical Results
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Timeline sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {summaryData.recentResults}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Results (Last 30 Days)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" color="secondary.main">
                {summaryData.testTypes}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Different Test Types
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Main Lab Reports Component
const LabReports = ({ 
  patientId, 
  patient,
  initialTab = 0,
  onClose,
  showPatientHeader = true 
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [dateRange, setDateRange] = useState({
    start: subDays(new Date(), 365), // Default to last 1 year to ensure mock data is included
    end: new Date()
  });
  const [anchorEl, setAnchorEl] = useState(null);
  
  const { reports, results, testDefinitions, loading, error } = useLabData(patientId, dateRange);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handleExport = (format) => {
    // TODO: Implement export functionality
    console.log(`Exporting as ${format}`);
    setAnchorEl(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading lab reports...</Typography>
      </Box>
    );
  }

  if (error && !error.includes('Using demo data')) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%' }}>
        {/* Patient Header */}
        {showPatientHeader && patient && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
            <Typography variant="h6" gutterBottom>
              Lab Reports - {patient.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip label={`MRN: ${patient.mrn || 'N/A'}`} size="small" />
              <Chip label={`DOB: ${patient.dateOfBirth ? format(new Date(patient.dateOfBirth), 'MM/dd/yyyy') : 'N/A'}`} size="small" />
              <Chip label={patient.gender || 'N/A'} size="small" />
            </Box>
          </Paper>
        )}

        {/* Demo Data Warning */}
        {error && error.includes('Using demo data') && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Controls */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <DatePicker
                label="Start Date"
                value={dateRange.start}
                onChange={(value) => handleDateRangeChange('start', value)}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="End Date"
                value={dateRange.end}
                onChange={(value) => handleDateRangeChange('end', value)}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<Download />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                variant="outlined"
                size="small"
              >
                Export
              </Button>
              <Button
                startIcon={<Print />}
                onClick={() => window.print()}
                variant="outlined"
                size="small"
              >
                Print
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Export Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => handleExport('pdf')}>
            <ListItemIcon><Download /></ListItemIcon>
            <ListItemText>Export as PDF</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleExport('excel')}>
            <ListItemIcon><TableChart /></ListItemIcon>
            <ListItemText>Export as Excel</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleExport('csv')}>
            <ListItemIcon><Assessment /></ListItemIcon>
            <ListItemText>Export as CSV</ListItemText>
          </MenuItem>
        </Menu>

        {/* Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            variant="scrollable" 
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              label="Trends & Values" 
              icon={<TrendingUp />} 
              iconPosition="start"
            />
            <Tab 
              label="Reports by Date" 
              icon={<Timeline />} 
              iconPosition="start"
            />
            <Tab 
              label="Summary" 
              icon={<Assessment />} 
              iconPosition="start"
            />
          </Tabs>

          {/* Tab Content */}
          {activeTab === 0 && (
            <TrendsTab 
              results={results} 
              reports={reports} 
              testDefinitions={testDefinitions}
              dateRange={dateRange}
            />
          )}
          {activeTab === 1 && (
            <ReportsTab 
              reports={reports} 
              results={results}
            />
          )}
          {activeTab === 2 && (
            <SummaryTab 
              reports={reports} 
              results={results} 
              testDefinitions={testDefinitions}
            />
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default LabReports; 